/** TODO: THIS ENTIRE FILE IS TEMPORARY, THE BACKEND WILL HAVE A v2 ENDPOINT SOON */

import { queryOptions } from "@tanstack/react-query";
import type { GenerateArticleResult } from "@/components/stealth-writer/GenerateArticle/ArticleGenerationService";
import type { ModifyTextResult } from "@/components/stealth-writer/result/ContentArea";
import type { TextAnalyzeResults } from "@/lib/textAnalyze";
import type { TextAnalyzeModelName } from "@/lib/types/provenance";
import type { PaginatedResponse, PaginationParams } from "@/lib/types";
import type { DateString } from "@/lib/utils/date";
import { api } from "@/lib/http";

export const historyModels = {
  AI_DETECTION: 1,
  SOURCE_DETECTION: 2,
  WRITING_ANALYSIS: 3,
  QUILLBOT_ANALYSIS: 4,
  AI_DETECTION_HIGHLIGHTER: 5,
  PLAGIARISM: 6,
  HUMANIZER: 7,
  AI_WRITER: 8,
  REWRITE: 9,
  SUMMARIZE: 10,
  EXPAND: 11,
  SHORTEN: 12,
  ADD_CONTENT: 13,
  PII_TEXT_DETECT: 14,

  1: "AI_DETECTION",
  2: "SOURCE_DETECTION",
  3: "WRITING_ANALYSIS",
  4: "QUILLBOT_ANALYSIS",
  5: "AI_DETECTION_HIGHLIGHTER",
  6: "PLAGIARISM",
  7: "HUMANIZER",
  8: "AI_WRITER",
  9: "REWRITE",
  10: "SUMMARIZE",
  11: "EXPAND",
  12: "SHORTEN",
  13: "ADD_CONTENT",
  14: "PII_TEXT_DETECT",
} as const;

type HistoryModels = typeof historyModels;
type HistoryModelIndex = Extract<keyof HistoryModels, number>;
export type HistoryModelName = HistoryModels[HistoryModelIndex];

type AiWriterModelName = Exclude<
  HistoryModelName,
  TextAnalyzeModelName | "PII_TEXT_DETECT"
>;

type ModifyTextModel = Exclude<AiWriterModelName, "AI_WRITER">;

type HistoryPiiResult = {
  FIRSTNAME: number;
  LASTNAME: number;
  BUILDINGNUMBER: number;
  STREET: number;
  CITY: number;
  STATE: number;
  ZIPCODE: number;
  LANDMARK: number;
  TIMEDATE: number;
  JOBPOSITION: number;
  CURRENCY: number;
  ORGANIZATION: number;
  AMOUNT: number;
};

type GetHistoryModelName<T extends keyof HistoryModels> = T extends string
  ? T
  : HistoryModels[T];
type GetHistoryModelIndex<T extends keyof HistoryModels> = T extends number
  ? T
  : HistoryModels[T];
export type HistoryResult<TModelIndex extends keyof HistoryModels> =
  GetHistoryModelName<TModelIndex> extends TextAnalyzeModelName
    ? TextAnalyzeResults<GetHistoryModelName<TModelIndex>>
    : GetHistoryModelName<TModelIndex> extends AiWriterModelName
      ? GetHistoryModelName<TModelIndex> extends ModifyTextModel
        ? ModifyTextResult
        : GenerateArticleResult
      : GetHistoryModelName<TModelIndex> extends "PII_TEXT_DETECT"
        ? HistoryPiiResult
        : never;
export type HistoryEntry<TModel extends keyof HistoryModels> = {
  id: string;
  tokens_used: number;
  model: GetHistoryModelIndex<TModel>;
  results: HistoryResult<TModel>;
  created_at: DateString;
  parent_id?: string;
};

type HistoryListParams<TModels extends HistoryModelName = HistoryModelName> =
  PaginationParams & {
    models?: Array<TModels>;
  };

async function getHistoryList<
  TModels extends HistoryModelName = HistoryModelName,
>({ page, page_size, models }: HistoryListParams<TModels>) {
  const searchParams = new URLSearchParams();
  searchParams.set("page", page.toString());
  searchParams.set("page_size", page_size.toString());
  if (models) models.forEach((model) => searchParams.append("models", model));
  return await api.detector
    .get<
      PaginatedResponse<HistoryEntry<TModels>>
    >("ai-writer/history", { searchParams })
    .json();
}

async function getHistoryDetail<TModel extends HistoryModelName>(id: string) {
  return await api.detector
    .get<HistoryEntry<TModel>>(`v2/ai-detector/user-history/${id}`)
    .json();
}

const historyQueryKey = ["detector", "history"] as const;
export function historyListQueryOptions<
  TModels extends HistoryModelName = HistoryModelName,
>(params: HistoryListParams<TModels>) {
  return queryOptions({
    queryKey: [...historyQueryKey, params],
    async queryFn({ client }) {
      const history = await getHistoryList<TModels>(params);
      history.items.forEach((entry) =>
        client.setQueryData(
          historyDetailQueryOptions<TModels>(entry.id).queryKey,
          entry,
        ),
      );
      return history;
    },
  });
}

export function historyDetailQueryOptions<TModel extends HistoryModelName>(
  id: string,
) {
  return queryOptions({
    queryKey: [...historyQueryKey, id],
    queryFn: () => getHistoryDetail<TModel>(id),
  });
}
